import { Fragment } from "react";
import Link from "next/link";
import { KennzahlIcon } from "./icons";
import { wertText, type Kennzahl, type KennzahlTon } from "./typen";

const PILLE_NEUTRAL = "mt-1 lg:mt-0 min-h-6 inline-flex items-center gap-x-1 py-0.5 px-2 bg-surface text-surface-foreground rounded-md";
const PILLE_NEUTRAL_LINK =
  "mt-1 lg:mt-0 min-h-6 inline-flex items-center gap-x-1 py-0.5 px-2 bg-surface text-surface-foreground hover:bg-surface-hover focus:outline-hidden focus:bg-surface-focus rounded-md";

/**
 * Abzeichen wie im Block (dort Links): neutral = surface, Warnung = orange, Risiko = rot
 * (ok hat der Block nicht → surface). Als Link mit Hover/Fokus, ohne href als Text ohne beides.
 */
const PILLE_TEXT: Record<KennzahlTon, string> = {
  neutral: PILLE_NEUTRAL,
  ok: PILLE_NEUTRAL,
  warn: "mt-1 lg:mt-0 min-h-6 inline-flex items-center gap-x-1 py-0.5 px-2 text-orange-700 bg-orange-200/70 rounded-md dark:bg-orange-700 dark:text-orange-100",
  risiko: "mt-1 lg:mt-0 min-h-6 inline-flex items-center gap-x-1 py-0.5 px-2 text-red-700 bg-red-200/70 rounded-md dark:bg-red-700 dark:text-red-100",
};

const PILLE_LINK: Record<KennzahlTon, string> = {
  neutral: PILLE_NEUTRAL_LINK,
  ok: PILLE_NEUTRAL_LINK,
  warn: "mt-1 lg:mt-0 min-h-6 inline-flex items-center gap-x-1 py-0.5 px-2 text-orange-700 bg-orange-200/70 hover:bg-orange-200 focus:outline-hidden focus:bg-orange-200 rounded-md dark:bg-orange-700 dark:text-orange-100 dark:hover:bg-orange-800 dark:focus:bg-orange-800",
  risiko: "mt-1 lg:mt-0 min-h-6 inline-flex items-center gap-x-1 py-0.5 px-2 text-red-700 bg-red-200/70 hover:bg-red-200 focus:outline-hidden focus:bg-red-200 rounded-md dark:bg-red-700 dark:text-red-100 dark:hover:bg-red-800 dark:focus:bg-red-800",
};

/**
 * Kandidat B: Kennzahlen in einer Zeile mit Trennlinien und Status-Abzeichen.
 * Kann nicht: Aufschlüsselung, Trend „runter/gleich“ als Pfeil, Ton „ok“ als eigene Farbe,
 * Absprung ohne Abzeichen (der Link ist das Abzeichen), Hilfe-Tooltip (kein Datenfeld).
 * Abzeichen-Text: Trend-Text, sonst Zusatz.
 */
export function KennzahlKachelB({ kennzahlen }: { kennzahlen: Kennzahl[] }) {
  return (
    <div className="grid sm:grid-cols-4 border-y border-line-2">
      {kennzahlen.map((k) => {
        const text = wertText(k);
        const ton = k.ton ?? "neutral";
        const pilleText = k.trend?.text ?? k.zusatz;
        const warnGlyph = ton === "warn" || ton === "risiko";
        const hochGlyph = !warnGlyph && k.trend?.richtung === "hoch";
        const pilleInhalt = (
          <>
            {warnGlyph && (
              <svg className="inline-block size-3 self-center" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
              </svg>
            )}
            {hochGlyph && (
              <svg className="inline-block size-3 self-center" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z" />
              </svg>
            )}
            <span className="inline-block text-xs font-semibold">{pilleText}</span>
          </>
        );
        return (
          <Fragment key={k.label}>
            {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-kpi-cards/kpi-cards-with-dividers */}
            <div className="p-4 md:p-5 relative before:absolute before:top-0 before:inset-s-0 before:w-full before:h-px sm:before:w-px sm:before:h-full before:bg-line-2 first:before:bg-transparent">
              <div>
                {k.icon && <KennzahlIcon name={k.icon} className="shrink-0 size-5 text-muted-foreground-1" />}

                <div className="mt-3">
                  <p className="text-xs uppercase text-muted-foreground-1">{k.label}</p>
                  <div className="mt-1 lg:flex lg:justify-between lg:items-center">
                    <h3 className="text-xl sm:text-2xl font-semibold text-foreground">
                      {text === null ? <span role="img" aria-label="nicht verfügbar">—</span> : text}
                    </h3>
                    {pilleText &&
                      (k.href ? (
                        <Link className={PILLE_LINK[ton]} href={k.href}>
                          {pilleInhalt}
                        </Link>
                      ) : (
                        <span className={PILLE_TEXT[ton]}>{pilleInhalt}</span>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
